/**
 * A página que o professor abre pelo link. P12.
 *
 * Sem login, sem menu, sem nada do sistema: é a única tela do Pauta que
 * alguém de fora vê. Diz o que a coordenação precisa dele — *"duas aulas à
 * noite, na quinta"* — e **não diz de quê** (`PESQUISA-ESPM.md` §14.4).
 *
 * Quem não tem carga vê a mesma página sem o bloco do pedido: o texto de
 * cima muda, os campos não somem — e não aparece "0 horas" em lugar nenhum.
 */

import { useEffect, useMemo, useState } from 'react'

import { ErroApi, formularioPublico, type FormularioPublico as Dados } from '../api'
import { LegendaDaSemana, SemanaDeDisponibilidade } from '../components/SemanaDeDisponibilidade'
import { cobre, contagem, oQueEhOQuadradinho } from '../formulario'


const data = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })

export function FormularioPublico({ token }: { token: string }) {
  const [dados, setDados] = useState<Dados | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [marcados, setMarcados] = useState<Set<string>>(new Set())
  const [justificativa, setJustificativa] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [faltasDoServidor, setFaltasDoServidor] = useState<string[] | null>(null)
  const [gravado, setGravado] = useState<string | null>(null)

  useEffect(() => {
    formularioPublico
      .ler(token)
      .then((d) => {
        setDados(d)
        setMarcados(new Set(d.marcados))
        setJustificativa(d.justificativa ?? '')
        if (d.respondido_em) setGravado(d.respondido_em)
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível abrir o formulário.'))
  }, [token])

  const n = useMemo(
    () => (dados?.pedido ? contagem(dados.pedido.exigencias, dados.tempos, marcados) : []),
    [dados, marcados],
  )
  const fecha = dados ? cobre(dados.pedido, dados.tempos, marcados) : true

  if (erro) {
    return (
      <main className="formulario">
        <h1>Disponibilidade</h1>
        <p className="state state--pendencia" role="alert">{erro}</p>
      </main>
    )
  }
  if (!dados) return <main className="formulario" aria-busy="true" />

  const mudar = (chaves: string[], pode: boolean) => {
    setMarcados((atual) => {
      const novo = new Set(atual)
      for (const k of chaves) {
        if (pode) novo.add(k)
        else novo.delete(k)
      }
      return novo
    })
    setFaltasDoServidor(null)
    setGravado(null)
  }

  const enviar = async () => {
    setEnviando(true)
    try {
      const r = await formularioPublico.enviar(token, [...marcados], fecha ? undefined : justificativa)
      if (r.gravado) {
        setGravado(new Date().toISOString())
        setFaltasDoServidor(null)
      } else {
        setFaltasDoServidor(r.faltas)
      }
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível enviar.')
    } finally {
      setEnviando(false)
    }
  }

  const quadradinho = oQueEhOQuadradinho(dados.tempos, dados.creditos_por_tempo ?? 1)
  const primeiroNome = dados.professor.split(/\s+/)[0] ?? ''
  const titulo = primeiroNome.charAt(0) + primeiroNome.slice(1).toLowerCase()

  return (
    <main className="formulario">
      <header className="page-head">
        <h1>Disponibilidade{dados.periodo ? ` · ${dados.periodo}` : ''}</h1>
        <p className="page-head__note">
          {dados.escola} · {dados.professor} · responda até {data(dados.expira_em)}
        </p>
      </header>

      {dados.pedido ? (
        <section className="section formulario__pedido" aria-labelledby="pedido">
          <h2 id="pedido">{titulo}, a coordenação precisa que você libere horário para:</h2>
          <ul className="formulario__frases">
            {dados.frases.map((f, i) => {
              const e = dados.pedido!.exigencias[i]
              const ok = e ? n[i]! >= e.aulas : true
              return (
                <li key={f} className={e ? (ok ? 'formulario__ok' : 'formulario__falta') : undefined}>
                  {f}
                  {e && (
                    <span className="mono">
                      {' '}
                      — {ok ? 'coberto' : `marcados ${n[i]} de ${e.aulas}`}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
          <p className="field__hint">
            Cada quadradinho é {quadradinho}. Liberar mais do que o pedido ajuda a montar a grade.
          </p>
        </section>
      ) : (
        <section className="section formulario__pedido">
          <h2>{titulo}, marque os horários em que você pode dar aula.</h2>
          <p className="field__hint">Cada quadradinho é {quadradinho}. Marque todos os que servem.</p>
        </section>
      )}

      <section className="section">
        <LegendaDaSemana />
        <SemanaDeDisponibilidade
          turnos={dados.turnos}
          tempos={dados.tempos}
          marcados={marcados}
          onMudar={mudar}
        />
        <p className="field__hint">
          Toque num quadradinho para trocar. Tocar no nome do dia troca a coluna inteira daquele turno; no horário, a
          linha.
        </p>
      </section>

      <section className="section stack">
        {!fecha && (
          <>
            <p className="callout">
              O que você marcou ainda não cobre o pedido. Se não for possível mesmo, diga por quê — a coordenação recebe
              a explicação e conversa com você.
            </p>
            <label className="field">
              <span className="field__label">Por que não é possível</span>
              <textarea
                className="input"
                rows={3}
                maxLength={2000}
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
              />
            </label>
          </>
        )}
        {faltasDoServidor && (
          <ul className="state state--pendencia" role="alert">
            {faltasDoServidor.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}
        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={enviando || (!fecha && !justificativa.trim())}
            onClick={() => void enviar()}
          >
            {enviando ? 'Enviando…' : 'Enviar'}
          </button>
          {gravado && (
            <span className="state state--ok" role="status">
              Recebido em {new Date(gravado).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}. Dá
              para mudar e enviar de novo até o prazo.
            </span>
          )}
        </div>
      </section>
    </main>
  )
}
